import { FC } from "react";
import InfoIcon from "@mui/icons-material/Info";
import { Alert, Chip, Tooltip } from "@mui/material";
import ceroPercent from "../../../assets/images/silosGrainWise/cero.svg";
import { showCorrectSVG } from "./binDesignCard";
import { BinSelectedInfo } from "../../../redux/slices/grainTracker/grainTrackerTypes";
import { v4 as uuidv4 } from "uuid";
import { SkeletonSelectedBinCard, SkeletonValues } from "./skeletenoBin";
import { LoadingButton } from "@mui/lab";

type SelectedBinTypes = {
  selectedBinValues: BinSelectedInfo | undefined;
  t: (arg0: string) => string;
  loading: boolean;
  resetStorage: (storageInstanceUUID: string) => void;
  loadingResetStorageCapacity: boolean;
  resetStorageCapacityError: string;
  isLocationShared: boolean;
};

export const formatValue = (value: number) => {
  const hasDecimals = value % 1 !== 0;

  if (value >= 1e6) {
    return hasDecimals ? value.toExponential(2) : value.toExponential(0);
  } else if (value >= 1000) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    });
  } else {
    return hasDecimals ? value.toFixed(2) : value.toString();
  }
};

export const formatValueWithTooltip = (value: number, unit: string) => {
  const fullValue = `${value.toFixed(2)} ${unit}`;
  const displayValue = `${formatValue(value)} ${unit}`;

  if (value >= 1e6) {
    return (
      <Tooltip title={fullValue} placement="top">
        <span style={{ cursor: "help" }}>{displayValue}</span>
      </Tooltip>
    );
  } else {
    return <span>{displayValue}</span>;
  }
};

const SelectedBin: FC<SelectedBinTypes> = ({
  selectedBinValues,
  t,
  loading,
  resetStorage,
  loadingResetStorageCapacity,
  resetStorageCapacityError,
  isLocationShared,
}) => {
  if (selectedBinValues) {
    const card = () => {
      const getPercentage =
        (100 * selectedBinValues?.usedCapacity?.value) /
        selectedBinValues?.totalCapacity?.value;

      const availableCapacity =
        selectedBinValues?.totalCapacity?.value -
        selectedBinValues?.usedCapacity?.value;

      return (
        <div className="selected-bin-container-card mb-2">
          <div className="title-selected-bin">
            {t("Selected_bin.storage_capacity")}
          </div>
          <div className="desc-selected-bin">
            <InfoIcon /> {t("Selected_bin.storage_capacity_desc")}
          </div>
          <div className="capacity-info-selected-bin">
            <div className="capacity-info-each">
              <Tooltip
                title={
                  selectedBinValues?.totalCapacity?.unit?.unitDescriptionTextId
                    .text
                }
                arrow
              >
                <div className="info-selected-bin">
                  <div>{t("Selected_bin.total_capacity")}</div>
                  <div>
                    {formatValueWithTooltip(
                      selectedBinValues?.totalCapacity?.value,
                      selectedBinValues?.totalCapacity?.unit?.unitSymbolTextId
                        ?.text
                    )}
                  </div>
                </div>
              </Tooltip>
              <Tooltip
                title={
                  selectedBinValues?.usedCapacity?.unit?.unitDescriptionTextId
                    ?.text
                }
                arrow
              >
                <div className="info-selected-bin-2">
                  <div>{t("Selected_bin.used")}</div>
                  <div>
                    {formatValueWithTooltip(
                      selectedBinValues?.usedCapacity?.value,
                      selectedBinValues?.totalCapacity?.unit?.unitSymbolTextId
                        ?.text
                    )}
                  </div>
                </div>
              </Tooltip>
              <div className="info-selected-bin-2">
                <div>{t("Selected_bin.avaliable")}</div>
                <div style={{ marginLeft: "15px" }}>
                  {formatValueWithTooltip(
                    availableCapacity,
                    selectedBinValues?.totalCapacity?.unit?.unitSymbolTextId
                      .text
                  )}
                </div>
              </div>
            </div>
            <div className="bin-svg-one">
              <img
                className="percentages-img-svg-location-empty-solo"
                src={ceroPercent}
                alt={"silo"}
              />
              {getPercentage >= 25 ? (
                <img
                  className={
                    getPercentage >= 100
                      ? "percentages-img-svg-location-hundred-solo"
                      : "percentages-img-svg-location-solo"
                  }
                  src={showCorrectSVG(getPercentage)}
                  style={{
                    backgroundColor: `${selectedBinValues?.cerealType?.iconBackgroundHexColor}`,
                  }}
                  alt={"silo"}
                />
              ) : (
                ""
              )}
            </div>
          </div>
          {!isLocationShared ? (
            <LoadingButton
              variant="outlined"
              color="error"
              loading={loadingResetStorageCapacity}
              fullWidth
              disabled={loadingResetStorageCapacity}
              onClick={() => resetStorage(selectedBinValues.id)}
              sx={{ textTransform: "none" }}
            >
              {t("Selected_bin.reset")}
            </LoadingButton>
          ) : (
            ""
          )}

          {resetStorageCapacityError ? (
            <Alert className="mt-2" severity="error">
              {resetStorageCapacityError}
            </Alert>
          ) : (
            ""
          )}
        </div>
      );
    };
    const characteristicsTypeAllotmentPolicy =
      selectedBinValues.currentCharacteristics.filter(
        (v) => v.type === "allotmentPolicy"
      );

    const characteristicsTypeCurrentState =
      selectedBinValues.currentCharacteristics.filter(
        (v) => v.type === "currentState"
      );

    return (
      <div className="selected-bin-container">
        {!loading || loadingResetStorageCapacity
          ? card()
          : SkeletonSelectedBinCard()}
        <div className="selected-bin-container-card-information">
          {!loading ? (
            <div className="selected-bin-container-info">
              <div className="title-card-info-bin-location-detail">
                {t("Selected_bin.allocation_plan")}
              </div>{" "}
              <div className="info-container-characteristic-site-view">
                <div className="values-card-info-bin-location-detail mr-2">
                  <div className="value-card-info-bin-location-detail">
                    <div className="value-title-card-info-bin-location-detail">
                      {t("Selected_bin.expected_species")}
                    </div>

                    <Chip
                      sx={{
                        backgroundColor: `${selectedBinValues?.cerealType?.iconBackgroundHexColor}`,
                        marginTop: "8px",
                      }}
                      label={selectedBinValues?.cerealType?.textId?.text}
                    />
                  </div>
                </div>

                <div className="info-container-characteristic-site-view-values">
                  {characteristicsTypeAllotmentPolicy
                    .map((values) => (
                      <div key={uuidv4()} className="mr-2 mb-2">
                        <div className="value-card-info-bin-location-detail-current-characteristics-name">
                          {values.name}
                        </div>
                        <div className="value-card-info-bin-location-detail-current-characteristics-values">
                          {values.value}
                        </div>
                      </div>
                    ))
                    .slice(1)}
                </div>
              </div>
            </div>
          ) : (
            SkeletonValues()
          )}

          {!loading ? (
            <div className="selected-bin-container-card-information mt-2">
              <div className="selected-bin-container-info">
                <div className="title-card-info-bin-location-detail">
                  {t("Selected_bin.average-cell")}
                </div>{" "}
                <div className="info-container-characteristic-site-view-values">
                  {characteristicsTypeCurrentState.length >= 1 ? (
                    characteristicsTypeCurrentState.map((values) => (
                      <div key={uuidv4()} className="mr-2 mb-2">
                        <div className="value-card-info-bin-location-detail-current-characteristics-name">
                          {values.name}
                        </div>
                        <div className="value-card-info-bin-location-detail-current-characteristics-values">
                          {values.value}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div>
                      <div className="value-card-info-bin-location-detail-current-characteristics-name-empty mb-2 mt-3">
                        {t("Selected_bin.no-data-text")}
                      </div>
                      <div className="value-card-info-bin-location-detail-current-characteristics-values-empty mb-5">
                        {" "}
                        {t("Selected_bin.no-data-desc")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            SkeletonValues()
          )}
        </div>
      </div>
    );
  } else {
    return "";
  }
};

export default SelectedBin;
